import type { ChatSessionListItem } from '@/components/chat-v2/hooks/useChatSession'

import { IS_FEDRAMP_ENVIRONMENT } from '@/constants/env'

import type { AgentName } from '@/types/chat-v2'
import type { UUID } from '@/types/Id'

export interface ChatSessionQueryKeyArgs {
  agentName: AgentName
  sessionId: UUID | ''
  orgId: UUID
}

export const chatSessionQueryKeys = {
  session: (args: ChatSessionQueryKeyArgs) =>
    ['chat-session', args.orgId, args.agentName, args.sessionId] as const,
  all: () => ['chat-session'] as const,
}

interface SimpleChatSessionQueryKeyArgs {
  sessionId: UUID | ''
}

export const simpleChatSessionQueryKeys = {
  session: (args: SimpleChatSessionQueryKeyArgs) =>
    ['chat-session', 'simple', args.sessionId] as const,
}

interface OrganizationChatSessionListQueryKeyArgs {
  agentName: 'organization_chat'
  orgId: UUID
}

interface OpportunityChatSessionListQueryKeyArgs {
  agentName: 'opportunity_chat'
  orgId: UUID
}

interface PursuitChatSessionListQueryKeyArgs {
  agentName: 'pursuit_chat'
  /** A specific pursuit, or null for every pursuit chat the viewer can access. */
  pursuitId: UUID | null
}

interface PursuitDocumentChatSessionListQueryKeyArgs {
  agentName: 'pursuit_document_chat'
  pursuitId: UUID
  documentId: UUID
}

export type ChatSessionListQueryKeyArgs =
  | OrganizationChatSessionListQueryKeyArgs
  | OpportunityChatSessionListQueryKeyArgs
  | PursuitChatSessionListQueryKeyArgs
  | PursuitDocumentChatSessionListQueryKeyArgs
/**
 * Session lists rendered by organization-level chat surfaces. FedRAMP does
 * not register opportunity-chat routes, so those builds never define that
 * query.
 */
export const getDefaultChatSessionListQueryKeys = (
  orgId: UUID,
  isFedrampEnvironment = IS_FEDRAMP_ENVIRONMENT,
): ChatSessionListQueryKeyArgs[] => {
  const queryKeys: ChatSessionListQueryKeyArgs[] = [
    { agentName: 'organization_chat', orgId },
  ]

  if (!isFedrampEnvironment) {
    queryKeys.push({ agentName: 'opportunity_chat', orgId })
  }

  queryKeys.push({ agentName: 'pursuit_chat', pursuitId: null })
  return queryKeys
}

interface SearchChatSessionListQueryKeyArgs {
  orgId: UUID
}

/**
 * Single owner of the session-list cache topology: which list a scope maps to
 * (`agent` and the per-agent methods) and which lists contain a given scope or
 * session (`forScope`/`forSession`). Consumers never encode per-agent key
 * knowledge themselves.
 */
export const chatSessionListQueryKeys = {
  all: () => ['chat-session-list'] as const,
  agent: (args: ChatSessionListQueryKeyArgs) => {
    switch (args.agentName) {
      case 'organization_chat':
        return chatSessionListQueryKeys.organization(args)
      case 'opportunity_chat':
        return chatSessionListQueryKeys.opportunity(args)
      case 'pursuit_chat':
        return chatSessionListQueryKeys.pursuit(args)
      case 'pursuit_document_chat':
        return chatSessionListQueryKeys.pursuitDocument(args)
      default: {
        const exhaustiveCheck: never = args
        return exhaustiveCheck
      }
    }
  },
  opportunity: (
    args: Omit<OpportunityChatSessionListQueryKeyArgs, 'agentName'>,
  ) => ['chat-session-list', 'opportunity_chat', args.orgId] as const,
  organization: (
    args: Omit<OrganizationChatSessionListQueryKeyArgs, 'agentName'>,
  ) => ['chat-session-list', 'organization_chat', args.orgId] as const,
  pursuit: (args: Omit<PursuitChatSessionListQueryKeyArgs, 'agentName'>) =>
    ['chat-session-list', 'pursuit_chat', args.pursuitId] as const,
  pursuitDocument: (
    args: Omit<PursuitDocumentChatSessionListQueryKeyArgs, 'agentName'>,
  ) =>
    [
      'chat-session-list',
      'pursuit_document_chat',
      args.pursuitId,
      args.documentId,
    ] as const,
  /**
   * The search-session list is rendered by search-chat's SearchSidebar rather
   * than chat-v2, but keyed in the same family so session mutations reach it.
   * `searchSessionQueryKeys.list` delegates here.
   */
  search: (args: SearchChatSessionListQueryKeyArgs) =>
    ['chat-session-list', 'search_chat', args.orgId] as const,
  /**
   * The exact list keys that contain a session scoped by `args`. A pursuit
   * session appears in both its specific list and the aggregate all-pursuits
   * list (`pursuitId: null`); every other agent has exactly one list.
   */
  forScope: (args: ChatSessionListQueryKeyArgs) =>
    args.agentName === 'pursuit_chat' && args.pursuitId !== null
      ? [
          chatSessionListQueryKeys.pursuit({ pursuitId: null }),
          chatSessionListQueryKeys.pursuit(args),
        ]
      : [chatSessionListQueryKeys.agent(args)],
  /**
   * The exact list keys that contain this session, including the
   * SearchSidebar-owned search list that lives in this same key family.
   * Form/spreadsheet sessions have no list queries anywhere in the app, so
   * they return `[]`; that is deliberately distinct from the `undefined`
   * fallback the mutation hooks use for "session unknown, mark every list
   * stale".
   */
  forSession: (session: ChatSessionListItem) => {
    switch (session.initial_agent_name) {
      case 'organization_chat':
        return chatSessionListQueryKeys.forScope({
          agentName: 'organization_chat',
          orgId: session.organization_id,
        })
      case 'opportunity_chat':
        return chatSessionListQueryKeys.forScope({
          agentName: 'opportunity_chat',
          orgId: session.organization_id,
        })
      case 'pursuit_chat':
        return chatSessionListQueryKeys.forScope({
          agentName: 'pursuit_chat',
          pursuitId: session.pursuit_id,
        })
      case 'pursuit_document_chat':
        return chatSessionListQueryKeys.forScope({
          agentName: 'pursuit_document_chat',
          pursuitId: session.pursuit_id,
          documentId: session.document_id,
        })
      case 'search_chat':
        return [
          chatSessionListQueryKeys.search({ orgId: session.organization_id }),
        ]
      case 'form_chat':
      case 'spreadsheet_chat':
        return []
      default: {
        const exhaustiveCheck: never = session
        return exhaustiveCheck
      }
    }
  },
}

export type ChatSessionListQueryKey =
  | ReturnType<typeof chatSessionListQueryKeys.agent>
  | ReturnType<typeof chatSessionListQueryKeys.search>
